import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { TypedUseSelectorHook } from 'react-redux';

import type { AppDispatch, RootState } from './store';

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

// For an input component that doesn't "commit" changes until blurred or unmounted
export function useSemicontrolledInput(args: {
  value: string;
  handleSaveValue: (value: string) => void;
  maxLength?: number;
}) {
  const { value, handleSaveValue, maxLength = Infinity } = args;

  const [transientValue, setTransientValue] = useState<string>(value);
  const [pristine, setPristine] = useState<boolean>(false);

  const saveChanges = useCallback(() => {
    if (
      transientValue.length <= maxLength ||
      // this horrible bit is to allow us to shorten the length of
      // values created before maxLength validation was introduced.
      // otherwise it's not possible to edit invalid values to make them
      // valid
      transientValue.length < value.length
    ) {
      if (transientValue !== value) {
        handleSaveValue(transientValue);
      }
      setPristine(true);
    }
  }, [handleSaveValue, transientValue, maxLength, value]);

  const onBlur = useCallback(() => {
    saveChanges();
  }, [saveChanges]);

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (
        e.target.value.length <= maxLength ||
        // this horrible bit is to allow us to shorten the length of
        // values created before maxLength validation was introduced.
        // otherwise it's not possible to edit invalid values to make them
        // valid
        e.target.value.length < transientValue.length
      ) {
        setTransientValue(e.target.value);
        setPristine(false);
      }
    },
    [maxLength, transientValue]
  );

  // if you don't have a ChangeEvent, use setValue to set it directly
  const setValue = useCallback(
    (val: string) => {
      if (
        val.length <= maxLength ||
        // this horrible bit is to allow us to shorten the length of
        // values created before maxLength validation was introduced.
        // otherwise it's not possible to edit invalid values to make them
        // valid
        val.length < transientValue.length
      ) {
        setTransientValue(val);
        setPristine(false);
      }
    },
    [maxLength, transientValue]
  );

  // if the value changes without onChange being called, when redux is updated
  // by another action for example, we need to update the transientValue
  useEffect(() => {
    setTransientValue(value);
  }, [value]);

  return {
    onBlur,
    onChange,
    value: pristine ? value : transientValue,
    setValue,
  };
}
